/**
 * Desktop My Interests (INVENTORY-DESKTOP §3.5, §5; prototype lines 250-317).
 * Lane D3 — the first of the two HTML5 drag surfaces.
 *
 * WHAT REPLACED WHAT. Mobile's board was a long-press drag with a move sheet
 * behind a tap, and a `TAP FOR OPTIONS · HOLD + DRAG TO MOVE OR REORDER` hint
 * to explain it. Desktop has none of those: there is no move sheet (§4), the
 * hint is gone, a capsule click opens Detail, and moving a game is a plain
 * HTML5 drag. `lib/drag.ts` is not involved at all (§5.6) — it is a pointer
 * state machine for touch, and none of its problems exist here.
 *
 * ROW KEYS are `interestsVM`'s own `rowId`: `s` · `a` · `b` · `c`. The drop
 * still goes through `intRowVote(rowId)` rather than using the key directly —
 * the mapping is the guard, not a convenience. Growing from three rows to five
 * and back to four changed nothing here: `useHtml5BoardDnd` and
 * `beforeIdToIdx` never enumerate the rows.
 *
 * THE FIFTH ROW IS NOW A PANEL (DESIGN-CHANGELOG 2026-08-24). `?` NO CALL YET
 * was a holding pen that grew with the crew's list and read as a rank below C.
 * The uncalled games queue in the 320px TO-DO panel beside the board instead —
 * the GOTY rail's twin, down to the geometry — and the panel is itself a drop
 * target: dropping a CALLED capsule on it runs `voteSet(id, '')`, which on this
 * tree is now the only way back to uncalled (the phone keeps `? NO CALL YET` in
 * its move sheet). It arms accent-2 only for a called game, because dragging
 * the featured cover onto its own panel is a no-op — `isCalledInt` is that gate,
 * exactly as `isPlacedTier` is the GOTY one.
 *
 * THE ONE CORRECTNESS TRAP (§10.5 risk 3). `beforeIdToIdx` converts the
 * prototype's "insert before this game id" into the index `setIntAt` wants —
 * and it must count over the row `setIntAt` will actually splice into, which is
 * rebuilt from the UNFILTERED list. `r.games` is the filtered row. With a
 * filter on, the two differ, so the index comes from
 * `intDropRowIds(state, rowId)` and never from what is on screen.
 *
 * DRAG STATE IS LOCAL (§5.6): `useHtml5BoardDnd` holds `dragId` / `overRow` /
 * `overBeforeId` in a `useReducer`. The store's `drag` / `dragOver` are
 * index-based and mobile-only; this screen never calls `dragStart`,
 * `setDragOver` or `dragEnd`, so `interestsVM`'s `shiftX` / `dragOp` (which key
 * off them) stay at their resting values and are ignored here in favour of the
 * §5.5 desktop feedback values.
 */

import { CoverArt } from '../../components/CoverArt.tsx';
import { useArt, useQueueArt } from '../../components/useArt.ts';
import type { GameId } from '../../lib/types.ts';
import { useQd } from '../../store/index.ts';
import {
  intDropRowIds,
  dragRing,
  dragRing2,
  intRowBord,
  intRowVote,
  intTodoBord,
  isCalledInt,
  rowMinH,
} from '../../store/selectors.d3.ts';
import { interestsVM, INT_SKIP_LAST, INT_SKIP_TIP, INT_TODO_LEGEND, INT_TODO_TITLE } from '../../store/selectors.ts';
import type { IntListRow } from '../../store/selectors.ts';
import { SearchBox } from '../../components/SearchBox.tsx';
import { TodoThumb } from '../../components/TodoThumb.tsx';
import BoardCapsule from '../components/BoardCapsule.tsx';
import FilterBar from '../components/FilterBar.tsx';
import PageBar from '../components/PageBar.tsx';
import { TodoFeaturedCover } from '../components/TodoPanel.tsx';
import { beforeIdToIdx } from '../dragHtml5.ts';
import type { BeforeId } from '../dragHtml5.ts';
import { useSearchInputRef } from '../searchHotkey.ts';
import { useHtml5BoardDnd } from '../useHtml5BoardDnd.ts';
import type { BoardDnd } from '../useHtml5BoardDnd.ts';

const MONO = 'var(--gt-font-mono)';
const PIXEL = 'var(--gt-font-pixel)';

type IntBoardGame = ReturnType<typeof interestsVM>['intBoardRows'][number]['games'][number];

/** A draggable board capsule. Every style value below comes from §5.5. */
function BoardTile({
  g,
  dnd,
  rowId,
  rowIds,
}: {
  g: IntBoardGame;
  dnd: BoardDnd;
  rowId: string;
  rowIds: readonly GameId[];
}): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const vis = dnd.capVis(rowId, rowIds, g.id);

  return (
    <BoardCapsule
      id={g.id}
      nameUp={g.nameUp}
      fall={g.fall}
      ini={g.ini}
      coverCands={g.coverCands}
      filt={g.filt}
      dnd={dnd}
      shift={vis.shift}
      onClick={() => openGame(g.id)}
      style={{
        width: vis.capW,
        marginLeft: vis.capMl,
        border: vis.bord,
        opacity: vis.op,
        transition: vis.trans,
        cursor: 'grab',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '6px 7px',
          fontFamily: MONO,
          fontSize: 9,
          color: 'var(--gt-text-3)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {g.nameUp}
      </div>
    </BoardCapsule>
  );
}

/** LIST mode row — the 460×215 header art, not the portrait (INVENTORY §11). */
function ListRow({ g }: { g: IntListRow }): JSX.Element {
  const openGame = useQd((s) => s.openGame);
  const vote = useQd((s) => s.vote);
  const art = useArt(g.id + 'w', g.hdrCands);

  return (
    <div
      onClick={() => openGame(g.id)}
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        border: '1px solid var(--gt-line)',
        background: 'var(--gt-card)',
        padding: '8px 12px',
        cursor: 'pointer',
        flexWrap: 'wrap',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: 92,
          flex: 'none',
          aspectRatio: '460/215',
          background: g.fall,
          overflow: 'hidden',
        }}
      >
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={9} ini={g.ini} hasCover={art.hasCover} cover={art.cover} />
      </div>
      <div style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ fontWeight: 600, fontSize: '12.5px', color: 'var(--gt-text)' }}>{g.name}</div>
        <div style={{ fontFamily: MONO, fontSize: '9.5px', color: 'var(--gt-faint)' }}>{g.metaTxt}</div>
      </div>
      {/* The four calls, in `CALLS` order. `vote()` is a toggle, so clicking the
          filled square clears back to no call — the `?` row on the board is the
          only other way to get there. The tooltip is the label the letter stands
          for; the square itself has room for one character. */}
      <div style={{ display: 'flex', gap: 4 }}>
        {g.calls.map((c) => (
          <span
            key={c.key}
            onClick={(e) => {
              e.stopPropagation();
              void vote(g.id, c.key);
            }}
            data-tip={c.tip}
            style={{
              width: 26,
              height: 26,
              border: '1px solid ' + c.bd,
              color: c.clr,
              background: c.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: MONO,
              fontSize: 11,
              cursor: 'pointer',
              boxSizing: 'border-box',
            }}
          >
            {c.g}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Interests(): JSX.Element {
  const s = useQd();
  const v = interestsVM(s);

  // Same art fetch the other party-list screens run — see `screens/Tonight.tsx`.
  useQueueArt(s.list.map((e) => e.id));
  // `/` target while MY INTERESTS is up (`desktop/searchHotkey.ts`).
  const bqRef = useSearchInputRef();
  const dnd = useHtml5BoardDnd();

  /**
   * The drop. Read the store fresh rather than through the render closure: a
   * drop lands after an arbitrary number of `dragover` frames and, on a slow
   * commit, after another tab's realtime write.
   */
  const drop = (rowId: string, id: GameId, before: BeforeId): void => {
    const st = useQd.getState();
    const vote = intRowVote(rowId);
    void st.setIntAt(id, vote, beforeIdToIdx(intDropRowIds(st, rowId), id, before));
  };

  /** `iSkip`: the last game left to call cannot be rotated away from. */
  const skip = (): void => {
    if (!v.hasIFeat) return;
    if (v.iSkipBlocked) {
      s.toast(INT_SKIP_LAST);
      return;
    }
    void s.intSkip(v.iFeat.id);
  };

  return (
    <div data-screen-label="My interests" style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Full screen since DESIGN-CHANGELOG 2026-09-03: no app header — this
          bar is the page's own, the same shape as GAME NIGHT and GAME DETAIL.
          The title lives in it; the page's controls stay down here. */}
      <PageBar title="MY INTERESTS" />
      {/* search · filter bar · segmented — the page's own first row ---------- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 26px 0', flexWrap: 'wrap' }}>
        {/* No explainer sub-line. The `YOUR CALL PER GAME — S MUST PLAY · …`
            ladder that sat here (porting/V.md §4) was cut on 2026-08-27 —
            the rows carry their own letters and mobile has never had it. */}
        {/* The box and + FILTERS are always adjacent and always in that order,
            on every board and both trees. */}
        <SearchBox
          value={v.bq}
          onChange={(e) => s.setBq('interests', e.target.value)}
          onClear={() => s.setBq('interests', '')}
          inputRef={bqRef}
          placeholder="SEARCH MY INTERESTS…"
          w="230px"
          pfs={11}
          ifs={10.5}
          pad="7px 10px"
        />
        <FilterBar />
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', fontFamily: MONO, fontSize: '10.5px' }}>
          <div
            onClick={() => s.setIMode('board')}
            style={{
              border: '1px solid ' + v.ibBd,
              color: v.ibClr,
              background: v.ibBg,
              padding: '8px 12px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            ▦ BOARD
          </div>
          <div
            onClick={() => s.setIMode('list')}
            style={{
              // Longhands, not `border` + `borderLeft:0` — mixing the shorthand
              // with a longhand makes React warn when the colour changes.
              borderTop: '1px solid ' + v.ilBd,
              borderRight: '1px solid ' + v.ilBd,
              borderBottom: '1px solid ' + v.ilBd,
              borderLeft: 0,
              color: v.ilClr,
              background: v.ilBg,
              padding: '8px 12px',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            ≡ LIST
          </div>
        </div>
      </div>

      {/* BOARD — four wrapping rows beside the TO-DO panel ------------------ */}
      {v.iBoard ? (
        <div style={{ display: 'flex', gap: 22, padding: '18px 26px 30px', alignItems: 'stretch', flexWrap: 'wrap' }}>
        <div style={{ flex: 2, minWidth: 340, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {v.intBoardRows.map((r) => (
            <div
              key={r.key}
              {...dnd.rowProps(r.rowId, (id, before) => drop(r.rowId, id, before))}
              style={{
                display: 'flex',
                border: intRowBord(r.bord, dnd.dragOn),
                // The armed weight, as an outline — see `borderArmed`. A wider
                // BORDER here is what made the last capsule on a full line wrap
                // away at dragstart and cancel the drag.
                ...dragRing(dnd.dragOn),
                background: 'var(--gt-card)',
              }}
            >
              <div
                style={{
                  width: 104,
                  flex: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                  background: r.labBg,
                  borderRight: '1px solid var(--gt-border)',
                  padding: '16px 8px',
                }}
              >
                <div style={{ fontFamily: PIXEL, fontSize: 15, color: r.bigClr, textShadow: 'var(--gt-shadow-btn)' }}>
                  {r.big}
                </div>
                <div
                  style={{
                    fontFamily: MONO,
                    fontSize: '8.5px',
                    letterSpacing: '.1em',
                    color: 'var(--gt-text-5)',
                    textAlign: 'center',
                  }}
                >
                  {r.sub}
                </div>
                {/* the third rail line Tonight omits (§3.5) */}
                <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-faint)' }}>{r.cntTxt}</div>
              </div>
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 12,
                  padding: 13,
                  alignContent: 'flex-start',
                  alignItems: 'flex-start',
                  minHeight: rowMinH(dnd.dragOn, dnd.overRow, r.rowId),
                  transition: 'min-height .16s ease',
                }}
              >
                {r.games.map((g) => (
                  <BoardTile
                    key={g.key}
                    g={g}
                    dnd={dnd}
                    rowId={r.rowId}
                    rowIds={r.games.map((x) => x.id)}
                  />
                ))}
                {r.isEmpty ? (
                  <div
                    style={{
                      alignSelf: 'center',
                      fontFamily: MONO,
                      fontSize: 10,
                      color: 'var(--gt-ghost)',
                      padding: '8px 2px',
                    }}
                  >
                    NOTHING HERE YET
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>

        {/* TO-DO panel — itself a drop target: drop a CALLED game to clear it */}
        <div
          {...dnd.zoneProps((id) => void useQd.getState().voteSet(id, ''))}
          title={INT_TODO_TITLE}
          style={{
            width: 320,
            flex: 'none',
            display: 'flex',
            flexDirection: 'column',
            border: intTodoBord(dnd.dragOn, isCalledInt(s, dnd.dragId)),
            // Layout-neutral for the same reason: this panel is a flex sibling
            // of the rows column, so a wider border here narrows the rows.
            ...dragRing2(dnd.dragOn && isCalledInt(s, dnd.dragId)),
            background: 'var(--gt-panel)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 14px',
              borderBottom: '1px dashed var(--gt-border-3)',
              flex: 'none',
            }}
          >
            <div style={{ fontFamily: PIXEL, fontSize: 7, color: 'var(--gt-text-5)' }}>TO DO</div>
            <div style={{ fontFamily: MONO, fontSize: '9.5px', color: 'var(--gt-faint)' }}>ONE AT A TIME</div>
            <div style={{ flex: 1 }} />
            <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--acc2)' }}>{v.iLeftTxt}</div>
          </div>

          {v.hasIFeat ? (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                padding: 14,
              }}
            >
              <TodoFeaturedCover feat={v.iFeat} dnd={dnd} tiltNs="int-todo" />

              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div
                  onClick={() => s.openGame(v.iFeat.id)}
                  style={{ fontWeight: 600, fontSize: 14, color: 'var(--gt-text)', cursor: 'pointer' }}
                >
                  {v.iFeat.name}
                </div>
                <div style={{ fontFamily: MONO, fontSize: '9.5px', color: 'var(--gt-faint)' }}>{v.iFeat.metaTxt}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
                {v.iBtns.map((b) => (
                  <div
                    key={b.key}
                    onClick={() => void s.voteSet(b.id, b.key)}
                    data-tip={b.tip}
                    style={{
                      height: 34,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: PIXEL,
                      fontSize: 10,
                      border: '1px solid ' + b.bd,
                      color: b.clr,
                      background: b.bg,
                      cursor: 'pointer',
                      boxSizing: 'border-box',
                    }}
                  >
                    {b.t}
                  </div>
                ))}
              </div>

              <div
                style={{
                  fontFamily: MONO,
                  fontSize: '8.5px',
                  color: 'var(--gt-tick)',
                  textAlign: 'center',
                  lineHeight: 1.8,
                }}
              >
                {INT_TODO_LEGEND.map((line, i) => (
                  <span key={line}>
                    {i ? <br /> : null}
                    {line}
                  </span>
                ))}
              </div>

              <div
                onClick={skip}
                data-tip={INT_SKIP_TIP}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                  border: '1px solid var(--gt-border-2)',
                  color: 'var(--gt-text-5)',
                  fontFamily: MONO,
                  fontSize: 10,
                  letterSpacing: '.05em',
                  padding: '9px 0',
                  cursor: 'pointer',
                }}
              >
                SKIP FOR NOW ↻
              </div>

              {v.hasIUpNext ? (
                <div
                  style={{
                    borderTop: '1px dashed var(--gt-border)',
                    paddingTop: 12,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.08em', color: 'var(--gt-faint)' }}>
                    UP NEXT
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {v.iUpNext.map((n) => (
                      <TodoThumb key={n.key} n={n} w={48} fs={8} />
                    ))}
                    {v.hasIMore ? (
                      <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-tick)' }}>{v.iMoreTxt}</div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                padding: '34px 16px',
                textAlign: 'center',
              }}
            >
              {v.iAllDone ? <div style={{ fontFamily: PIXEL, fontSize: 14, color: 'var(--acc)' }}>✓</div> : null}
              <div style={{ fontFamily: MONO, fontSize: 10, color: 'var(--gt-muted)', lineHeight: 1.9 }}>
                {v.iDoneTxt}
              </div>
            </div>
          )}
        </div>
        </div>
      ) : null}

      {/* LIST -------------------------------------------------------------- */}
      {v.iList ? (
        <div style={{ display: 'flex', gap: 22, padding: '14px 26px 30px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 320, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: 10, fontFamily: MONO, fontSize: 10, color: 'var(--gt-faint)' }}
            >
              <span>ALL GAMES · {v.intCountTxt}</span>
              <div style={{ flex: 1, borderTop: '1px dashed var(--gt-border)' }} />
              <span>TAP S · A · B · C TO SET YOUR CALL</span>
            </div>
            {v.intRows.map((g) => (
              <ListRow key={g.key} g={g} />
            ))}
            {v.intRows.length === 0 ? (
              <div
                style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  fontFamily: MONO,
                  fontSize: 11,
                  color: 'var(--gt-muted)',
                  lineHeight: 2,
                }}
              >
                {v.tEmptyTxt}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
